/* eslint-disable import/no-relative-packages */

// eslint-disable-next-line import/no-extraneous-dependencies
import { print } from 'graphql/language/printer';

import { Fragment_ArticleCard } from '../yi-core/src/gql/generated/Fragment_ArticleCard.ts';
import { Fragment_ClassCard } from '../yi-core/src/gql/generated/Fragment_ClassCard.ts';
import { Fragment_CourseCard } from '../yi-core/src/gql/generated/Fragment_CourseCard.ts';
import { Fragment_LiveCard } from '../yi-core/src/gql/generated/Fragment_LiveCard.ts';
import { Fragment_MeditationCard } from '../yi-core/src/gql/generated/Fragment_MeditationCard.ts';
import { Fragment_PoseCard } from '../yi-core/src/gql/generated/Fragment_PoseCard.ts';
import { Fragment_ProgramCard } from '../yi-core/src/gql/generated/Fragment_ProgramCard.ts';
import { Fragment_SeriesCard } from '../yi-core/src/gql/generated/Fragment_SeriesCard.ts';
import { Fragment_TeacherCard } from '../yi-core/src/gql/generated/Fragment_TeacherCard.ts';

const makeCoursesQuery = (language, moreFilters = []) => {
  const filters = [
    `language: ${language}`,
    `excludeCategoryIds: [272]`, // exclude hidden courses
    ...moreFilters,
  ];
  return `
      allCourses(filter: { ${filters.join(',')} }) {
        __typename
        objectID:id
        body:snippet(limit:${language === 'ENGLISH' ? 17000 : 15000})
        action_type
        title
        recent_minutes_watched
        entry_date {
          timestamp
        }
        categories {
          name
        }
        level {
          name
        }
        style {
          name
        }
        goals {
          id
          name
        }
        teachers {
          full_name
        }
        permissions
        algolia_focus_filters {
          name
        }
        priority
        ${extractFragmentBodyString(Fragment_CourseCard)}
      }
  `;
};
// Gatsby fragments not accessible when gatsby-config is read.
// Cannot use entire fragment string due to YI_ prefix on SSR types,
// so we extract just the fragment body and insert it.
export const extractFragmentBodyString = gqlFrag => {
  const fragStr = print(gqlFrag);
  return fragStr.slice(fragStr.indexOf('{') + 1, fragStr.lastIndexOf('}'));
};

export const languages = [
  {
    name: 'ENGLISH',
    code: 'en',
  },
  {
    name: 'SPANISH',
    code: 'es',
  },
];
const commonUnretrievableAttributes = ['body', 'objectID'];

// Exposed for client-side query string creation/parsing
export const facetingAttributePrefix = 'ALGOLIA_';

// Algolia's default ranking criteria. They recommend NOT changing this
const defaultRanking = ['typo', 'geo', 'words', 'filters', 'proximity', 'attribute', 'exact'];
const commonRanking = [...defaultRanking, 'desc(entry_date.timestamp)'];
const courseRanking = [
  ...defaultRanking,
  'desc(recent_minutes_watched)',
  'desc(entry_date.timestamp)',
];

function getAllArticlesQuery(language) {
  return `
    allArticles(filter: {language: ${language}}) {
      objectID:id
      __typename
      body:snippet(limit:${language === 'ENGLISH' ? 17000 : 15000})
      title
      entry_date {
        timestamp
      }
      teacher {
        full_name
      }
      topic {
        name
      }
      tags {
        text
      }
      priority
      ${extractFragmentBodyString(Fragment_ArticleCard)}
    }
`;
}

function getAllClassesQuery(language) {
  return `
        allClasses(filter: { practiceType: DEFAULT, language: ${language} }) {
          __typename
          objectID:id
          body:snippet(limit:${language === 'ENGLISH' ? 17000 : 15000})
          title
          completed_count
          recent_completed_count_group
          completion_rate
          recent_minutes_watched
          entry_date {
            timestamp
          }
          teacher {
            full_name
          }
          level {
            name
          }
          style {
            name
          }
          anatomy {
            name
          }
          media_duration {
            group
          }
          time_of_day {
            name
          }
          goals {
            name
          }
          props {
            name
          }
          tags {
            text
          }
          algolia_focus_filters {
            name
          }
          algolia_has_closed_captions
          algolia_has_no_props
          ${extractFragmentBodyString(Fragment_ClassCard)}
        }
`;
}

function getAllPranayamaClasses(language) {
  return `
      allClasses(filter: { practiceType: PRANAYAMA, language: ${language} }) {
        objectID:id
        __typename
        body:snippet(limit:${language === 'ENGLISH' ? 17000 : 15000})
        title
        completed_count
        recent_completed_count_group
        completion_rate
        recent_minutes_watched
        entry_date {
          timestamp
        }
        teacher {
          full_name
        }
        level {
          name
        }
        style {
          name
        }
        focus {
          name
        }
        anatomy {
          name
        }
        media_duration {
          group
        }
        time_of_day {
          name
        }
        goals {
          name
        }
        props {
          name
        }
        tags {
          text
        }
        algolia_focus_filters {
          name
        }
        algolia_has_closed_captions
        algolia_has_no_props
        ${extractFragmentBodyString(Fragment_ClassCard)}
      }
  `;
}

function getAllPranayamaMeditations(language) {
  return `
    allMeditations(filter: { practiceType: PRANAYAMA, language: ${language} }) {
      __typename
      objectID:id
      body:snippet(limit:${language === 'ENGLISH' ? 17000 : 15000})
      title
      completed_count
      recent_completed_count_group
      completion_rate
      recent_minutes_watched
      entry_date {
        timestamp
      }
      teacher {
        full_name
      }
      style {
        id
        name
      }
      focus {
        name
      }
      media_duration {
        group
      }
      time_of_day {
        name
      }
      goals {
        name
      }
      level {
        name
      }
      algolia_focus_filters {
        name
      }
      algolia_has_closed_captions
      algolia_has_no_props
      ${extractFragmentBodyString(Fragment_MeditationCard)}
    }
`;
}

function getAllMeditations(language) {
  return `
        allMeditations(filter: { practiceType: DEFAULT, language: ${language} }) {
          __typename
          objectID:id
          body:snippet(limit:${language === 'ENGLISH' ? 17000 : 15000})
          title
          completed_count
          recent_completed_count_group
          completion_rate
          recent_minutes_watched
          entry_date {
            timestamp
          }
          teacher {
            full_name
          }
          style {
            id
            name
          }
          focus {
            name
          }
          media_duration {
            group
          }
          time_of_day {
            name
          }
          goals {
            name
          }
          level {
            name
          }
          algolia_focus_filters {
            name
          }
          algolia_has_closed_captions
          algolia_has_no_props
          ${extractFragmentBodyString(Fragment_MeditationCard)}
        }
`;
}

function getAllPodcasts(language) {
  return makeCoursesQuery(language, ['courseType: PODCAST']);
}

function getAllPremiumTrainings(language) {
  return makeCoursesQuery(language, ['courseType: PREMIUM_TRAINING']);
}

function getAllChallenges(language) {
  return makeCoursesQuery(language, ['courseType: CHALLENGE']);
}

function getAllCourses(language) {
  return makeCoursesQuery(language, ['courseType: WORKSHOP']);
}

function getAllTeacher(language) {
  return `
        allTeachers(filter: {language: ${language}}) {
          __typename
          objectID:id
          body:snippet(limit:${language === 'ENGLISH' ? 17000 : 15000})
          full_name
          styles {
            name
          }
          entry_date {
            timestamp
          }
          ${extractFragmentBodyString(Fragment_TeacherCard)}
        }
`;
}
function getAllSeries(language) {
  return `
    allSeries(filter: { language: ${language} }) {
      __typename
      objectID:id
      id
      ${extractFragmentBodyString(Fragment_SeriesCard)}
    }
`;
}

const algoliaConfigs = [
  {
    indexName: 'articles',
    refinements: [
      {
        facetingAttributeName: 'teacher',
        getFieldValue: item => item.teacher.full_name,
      },
      {
        facetingAttributeName: 'topic',
        getFieldValue: item => item.topic.name,
      },
      // TODO 'tag' and 'focus' is equal. 'tag' should be removed after we migrate to the new filters structure on all devices
      {
        facetingAttributeName: 'tag',
        getFieldValue: item => item.tags.map(i => i.text),
      },
      {
        facetingAttributeName: 'focus',
        getFieldValue: item => item.tags.map(i => i.text),
      },
    ],
    searchableAttributes: ['title', 'body'],
    getContentItemsFromQueryResult: ({ yi }) => yi.allArticles,
    query(language) {
      return `{
      yi {
        ${getAllArticlesQuery(language)}
      }
    }`;
    },
  },
  {
    indexName: 'poses',
    refinements: [
      {
        facetingAttributeName: 'category',
        getFieldValue: item => item.pose_category.map(i => i.name),
      },
    ],
    searchableAttributes: ['title', 'body'],
    getContentItemsFromQueryResult: ({ yi }) => yi.allPoses,
    query(language) {
      return `{
      yi {
        allPoses(filter: {language: ${language}}) {
          __typename
          objectID:id
          body:snippet(limit:${language === 'ENGLISH' ? 17000 : 15000})
          title
          entry_date {
            timestamp
          }
          pose_category {
            name
          }
          ${extractFragmentBodyString(Fragment_PoseCard)}
        }
      }
    }`;
    },
  },
  {
    indexName: 'classes',
    refinements: [
      { facetingAttributeName: 'teacher', getFieldValue: item => item.teacher.full_name },
      {
        facetingAttributeName: 'duration',
        getFieldValue: item => item.media_duration.group,
      },
      { facetingAttributeName: 'level', getFieldValue: item => item.level.map(i => i.name) },
      { facetingAttributeName: 'style', getFieldValue: item => item.style.map(i => i.name) },
      {
        facetingAttributeName: 'anatomy',
        getFieldValue: item => item.anatomy.map(i => i.name),
      },
      {
        facetingAttributeName: 'time',
        getFieldValue: item => item.time_of_day.map(i => i.name),
      },
      { facetingAttributeName: 'goals', getFieldValue: item => item.goals.map(i => i.name) },
      { facetingAttributeName: 'props', getFieldValue: item => item.props.map(i => i.name) },
      { facetingAttributeName: 'tags', getFieldValue: item => item.tags.map(i => i.text) },
      {
        facetingAttributeName: 'focus',
        getFieldValue: item => item.algolia_focus_filters.map(i => i.name),
      },
      {
        facetingAttributeName: 'closed_captions',
        getFieldValue: item => item.algolia_has_closed_captions,
      },
      {
        facetingAttributeName: 'has_no_props',
        getFieldValue: item => item.algolia_has_no_props,
      },
    ],
    searchableAttributes: ['title', 'body'],
    attributeForDistinct: 'ALGOLIA_teacher',
    ranking: [
      ...defaultRanking,
      'desc(recent_completed_count_group)',
      'desc(completion_rate)',
      'desc(entry_date.timestamp)',
    ],
    getContentItemsFromQueryResult: ({ yi }) => yi.allClasses,
    query(language) {
      return `{
      yi {
        ${getAllClassesQuery(language)}
      }
    }`;
    },
  },
  {
    indexName: 'pranayamas',
    // Classes and meditations have the same `teacher` and `media_duration` field shapes, but different tags/focus
    refinements: [
      { facetingAttributeName: 'teacher', getFieldValue: item => item.teacher.full_name },
      {
        facetingAttributeName: 'duration',
        getFieldValue: item => item.media_duration.group,
      },
      {
        facetingAttributeName: 'focus',
        getFieldValue: item => {
          if (Array.isArray(item.focus)) return item.focus.map(i => i.name);
          return [];
        },
      },
      {
        // TODO rename algolia_focus -> focus, after we migrate to the new filters on all platforms
        // It named 'algolia_focus', because of the conflict with the existing faceting attr name
        facetingAttributeName: 'algolia_focus',
        getFieldValue: item => item.algolia_focus_filters.map(i => i.name),
      },
      {
        facetingAttributeName: 'closed_captions',
        getFieldValue: item => item.algolia_has_closed_captions,
      },
      {
        facetingAttributeName: 'has_no_props',
        getFieldValue: item => item.algolia_has_no_props,
      },
      {
        facetingAttributeName: 'level',
        getFieldValue: item => item?.level?.map(i => i.name) ?? [],
      },
    ],
    searchableAttributes: ['title', 'body'],
    ranking: [
      ...defaultRanking,
      'desc(recent_completed_count_group)',
      'desc(completion_rate)',
      'desc(entry_date.timestamp)',
    ],
    getContentItemsFromQueryResult: ({ yi }) => [...yi.allMeditations, ...yi.allClasses],
    query(language) {
      // @TODO DRY query with classes and meditations indices
      return `{
      yi {
        ${getAllPranayamaClasses(language)}
        ${getAllPranayamaMeditations(language)}
      }
    }`;
    },
  },
  {
    indexName: 'meditations',
    refinements: [
      { facetingAttributeName: 'teacher', getFieldValue: item => item.teacher.full_name },
      {
        facetingAttributeName: 'duration',
        getFieldValue: item => item.media_duration.group,
      },
      // TODO 'type' and 'style' is equal. 'type' should be removed after we migrate to the new filters structure on all devices
      { facetingAttributeName: 'type', getFieldValue: item => item.style.map(i => i.name) },
      { facetingAttributeName: 'style', getFieldValue: item => item.style.map(i => i.name) },
      { facetingAttributeName: 'tag', getFieldValue: item => item.focus.map(i => i.name) },
      {
        facetingAttributeName: 'time',
        getFieldValue: item => item.time_of_day.map(i => i.name),
      },
      { facetingAttributeName: 'goals', getFieldValue: item => item.goals.map(i => i.name) },
      {
        facetingAttributeName: 'focus',
        getFieldValue: item => item.algolia_focus_filters.map(i => i.name),
      },
      {
        facetingAttributeName: 'level',
        getFieldValue: item => item.level.map(i => i.name),
      },
      {
        facetingAttributeName: 'closed_captions',
        getFieldValue: item => item.algolia_has_closed_captions,
      },
      {
        facetingAttributeName: 'has_no_props',
        getFieldValue: item => item.algolia_has_no_props,
      },
    ],
    searchableAttributes: ['title', 'body'],
    ranking: [
      ...defaultRanking,
      'desc(recent_completed_count_group)',
      'desc(completion_rate)',
      'desc(entry_date.timestamp)',
    ],
    getContentItemsFromQueryResult: ({ yi }) => yi.allMeditations,
    query(language) {
      return `{
      yi {
        ${getAllMeditations(language)}
      }
    }`;
    },
  },
  {
    indexName: 'challenges',
    refinements: [
      {
        facetingAttributeName: 'category',
        getFieldValue: ({ categories }) => categories.map(i => i.name),
      },
      {
        facetingAttributeName: 'duration',
        getFieldValue: ({ duration }) => duration.text,
      },
      {
        facetingAttributeName: 'style',
        getFieldValue: item => item.style.map(i => i.name),
      },
      { facetingAttributeName: 'level', getFieldValue: item => item.level.map(i => i.name) },
      { facetingAttributeName: 'teacher', getFieldValue: item => item.teachers.full_name },
    ],
    searchableAttributes: ['title', 'body'],
    getContentItemsFromQueryResult: ({ yi }) => yi.allCourses,
    query(language) {
      return `{
        yi{
          allCourses(orderBy: createdAt_DESC, filter: { language: ${language}, categoryId: 274, excludeCategoryId: 272 }) {
            objectID:id
            body:snippet(limit:${language === 'ENGLISH' ? 17000 : 15000})
            title
            recent_minutes_watched
            entry_date {
              timestamp
            }
            duration {
              text
            }
            categories {
              name
            }
            level {
              name
            }
            style {
              name
            }
          ${extractFragmentBodyString(Fragment_ProgramCard)}
        }
      }
    }`;
    },
  },
  {
    indexName: 'community',
    refinements: [
      {
        facetingAttributeName: 'teacher',
        getFieldValue: ({ teachers }) => teachers.map(t => t.full_name),
      },
      {
        facetingAttributeName: 'category',
        getFieldValue: ({ categories }) => categories.map(c => c.name),
      },
    ],
    searchableAttributes: ['title', 'body', 'description'],
    getContentItemsFromQueryResult: ({ yi }) => yi.allCourses,
    query(language) {
      return `{
        yi {
          allCourses(orderBy: createdAt_DESC, filter: { language: ${language}, categoryId: 296, excludeCategoryId: 272 }) {
            objectID:id
            body:snippet(limit:${language === 'ENGLISH' ? 17000 : 15000})
            title
            recent_minutes_watched
            entry_date {
              timestamp
            }
            categories {
              name
            }
            teachers { 
              full_name
            }
            ${extractFragmentBodyString(Fragment_CourseCard)}
          }
        }
      }`;
    },
  },

  {
    indexName: 'podcasts',
    refinements: [
      {
        facetingAttributeName: 'teacher',
        getFieldValue: ({ teachers }) => teachers.map(t => t.full_name),
      },
      {
        facetingAttributeName: 'focus',
        getFieldValue: ({ categories }) => categories.map(i => i.name),
      },
    ],
    searchableAttributes: ['title', 'body', 'description'],
    getContentItemsFromQueryResult: ({ yi }) => yi.allCourses,
    query(language) {
      return `{
        yi {
          allCourses(orderBy: createdAt_DESC, filter: { language: ${language}, categoryId: 295 }) {
            objectID:id
            body:snippet(limit:${language === 'ENGLISH' ? 17000 : 15000})
            title
            recent_minutes_watched
            teachers { 
              full_name
            }
            ${extractFragmentBodyString(Fragment_CourseCard)}
          }
        }
      }`;
    },
  },

  {
    indexName: 'courses', // DEPRECATED -- use `courses[workshops]`
    refinements: [
      {
        facetingAttributeName: 'category',
        getFieldValue: ({ categories }) => categories.map(i => i.name),
      },
      {
        facetingAttributeName: 'goals',
        getFieldValue: ({ goals }) => goals.map(i => i.name),
      },
      {
        facetingAttributeName: 'teacher',
        getFieldValue: ({ teachers }) => teachers.map(t => t.full_name),
      },
      {
        facetingAttributeName: 'membership',
        getFieldValue: ({ permissions }) => permissions,
      },
      {
        facetingAttributeName: 'action',
        getFieldValue: ({ action_type }) => action_type,
      },
    ],
    searchableAttributes: ['title', 'body', 'description'],
    getContentItemsFromQueryResult: ({ yi }) => yi.allCourses,

    query(language) {
      return ` {
        yi {
          ${makeCoursesQuery(language)}
        }
      }
  `;
    },
  },

  {
    indexName: 'courses[premium_trainings]',
    refinements: [
      // TODO 'category' and 'focus' is equal. 'category' should be removed after we migrate to the new filters structure on all devices
      {
        facetingAttributeName: 'category',
        getFieldValue: item => item.categories.map(i => i.name),
      },
      {
        facetingAttributeName: 'focus',
        getFieldValue: item => item.categories.map(i => i.name),
      },
      {
        facetingAttributeName: 'goals',
        getFieldValue: ({ goals }) => goals.map(i => i.name),
      },
      {
        facetingAttributeName: 'teacher',
        getFieldValue: item => item.teachers.map(t => t.full_name),
      },
    ],
    searchableAttributes: ['title', 'body', 'description'],
    ranking: courseRanking,
    getContentItemsFromQueryResult: ({ yi }) => yi.allCourses,
    query(language) {
      return ` {
      yi {
        ${getAllPremiumTrainings(language)}
      }
    }
    `;
    },
  },
  {
    indexName: 'courses[challenges]',
    refinements: [
      // TODO 'category' and 'focus' is equal. 'category' should be removed after we migrate to the new filters structure on all devices
      {
        facetingAttributeName: 'category',
        getFieldValue: item => item.categories.map(i => i.name),
      },
      {
        facetingAttributeName: 'focus',
        getFieldValue: item => item.algolia_focus_filters.map(i => i.name),
      },
      {
        facetingAttributeName: 'goals',
        getFieldValue: ({ goals }) => goals.map(i => i.name),
      },
      { facetingAttributeName: 'level', getFieldValue: item => item.level.map(i => i.name) },
      { facetingAttributeName: 'style', getFieldValue: item => item.style.map(i => i.name) },
      {
        facetingAttributeName: 'teacher',
        getFieldValue: item => item.teachers.map(t => t.full_name),
      },
    ],
    searchableAttributes: ['title', 'body', 'description'],
    ranking: courseRanking,
    getContentItemsFromQueryResult: ({ yi }) => yi.allCourses,
    query(language) {
      return ` {
      yi {
        ${getAllChallenges(language)}
      }
    }
    `;
    },
  },
  {
    indexName: 'courses[workshops]',
    refinements: [
      {
        facetingAttributeName: 'category',
        getFieldValue: item => item.categories.map(i => i.name),
      },
      {
        facetingAttributeName: 'focus',
        getFieldValue: item => item.algolia_focus_filters.map(i => i.name),
      },
      {
        facetingAttributeName: 'goals',
        getFieldValue: ({ goals }) => goals.map(i => i.name),
      },
      {
        facetingAttributeName: 'teacher',
        getFieldValue: item => item.teachers.map(t => t.full_name),
      },
      { facetingAttributeName: 'level', getFieldValue: item => item.level.map(i => i.name) },
      { facetingAttributeName: 'style', getFieldValue: item => item.style.map(i => i.name) },
    ],
    searchableAttributes: ['title', 'body', 'description'],
    ranking: courseRanking,
    getContentItemsFromQueryResult: ({ yi }) => yi.allCourses,
    query(language) {
      return ` {
      yi {
        ${getAllCourses(language)}
      }
    }
    `;
    },
  },
  {
    indexName: 'courses[podcasts]',
    refinements: [
      // TODO 'category' and 'focus' is equal. 'category' should be removed after we migrate to the new filters structure on all devices
      {
        facetingAttributeName: 'category',
        getFieldValue: item => item.categories.map(i => i.name),
      },
      {
        facetingAttributeName: 'focus',
        getFieldValue: item => item.categories.map(i => i.name),
      },
      {
        facetingAttributeName: 'teacher',
        getFieldValue: item => item.teachers.map(t => t.full_name),
      },
    ],
    searchableAttributes: ['title', 'body', 'description'],
    ranking: courseRanking,
    getContentItemsFromQueryResult: ({ yi }) => yi.allCourses,

    query(language) {
      return ` {
      yi {
        ${getAllPodcasts(language)}
      }
    }
    `;
    },
  },
  {
    indexName: 'teachers',
    settings: {
      ranking: ['desc(entry_date.timestamp)'],
    },
    refinements: [
      {
        facetingAttributeName: 'style',
        getFieldValue: item => item.styles.map(i => i.name),
      },
    ],
    searchableAttributes: ['full_name', 'body'],
    getContentItemsFromQueryResult: ({ yi }) => yi.allTeachers,
    query(language) {
      return `{
      yi {
        ${getAllTeacher(language)}
      }
    }`;
    },
  },
  {
    indexName: 'live',
    refinements: [
      {
        facetingAttributeName: 'teacher',
        getFieldValue: item => item.teacher.full_name,
      },
      {
        facetingAttributeName: 'tags',
        getFieldValue: item => item.tags.map(i => i.name),
      },
      {
        facetingAttributeName: 'type',
        getFieldValue: item => item.action_type,
      },
    ],
    searchableAttributes: ['title', 'body'],
    getContentItemsFromQueryResult: ({ yi }) => yi.allLives,
    query(language) {
      return `{
      yi {
        allLives(filter: {language: ${language}}) {
          objectID:id
          body:snippet(limit:${language === 'ENGLISH' ? 17000 : 15000})
          ${extractFragmentBodyString(Fragment_LiveCard)}
        }
      }
    }`;
    },
  },
  {
    indexName: 'series',
    refinements: [],
    searchableAttributes: ['title'],
    ranking: [...defaultRanking, 'desc(entry_date.timestamp)'],
    getContentItemsFromQueryResult: ({ yi }) => yi.allSeries,
    query(language) {
      return `{
      yi {
        ${getAllSeries(language)}
      }
    }`;
    },
  },
];

const withPrefix = attributeName => `${facetingAttributePrefix}${attributeName}`;
export const algoliaQueries = [];
export const algoliaReplicaIndices = [];

const getCommonSettings = language => ({
  sortFacetValuesBy: 'alpha',
  ignorePlurals: true,
  removeStopWords: true,
  queryLanguages: [language],
  indexLanguages: [language],
});

const getReplicaIndices = (
  indexName,
  langCode,
  refinements = [],
  unretrievableAttributes,
  searchableAttributes = [],
  attributeForDistinct,
) => {
  const replicas = [];

  // -------------/ REPLICA INDICES \-------------

  // Only create Newest and Oldest replicas of indices other than teachers
  if (!indexName.includes('teachers') && !indexName.includes('site-wide-search')) {
    const dateField = indexName.includes('live') ? 'start_date' : 'entry_date';
    // Replica index: Newest
    replicas.push({
      indexName: `${indexName}_${langCode}_newest`,
      settings: {
        customRanking: [`asc(priority)`, `desc(${dateField}.timestamp)`],
        ranking: ['custom', ...commonRanking],
        unretrievableAttributes,
        searchableAttributes,
        attributesForFaceting: refinements.map(r => withPrefix(r.facetingAttributeName)),
        ...(attributeForDistinct && { attributeForDistinct }),
        ...getCommonSettings(langCode),
      },
    });

    // Replica index: Oldest
    replicas.push({
      indexName: `${indexName}_${langCode}_oldest`,
      settings: {
        customRanking: [`asc(${dateField}.timestamp)`],
        ranking: ['custom', ...commonRanking],
        unretrievableAttributes,
        searchableAttributes,
        attributesForFaceting: refinements.map(r => withPrefix(r.facetingAttributeName)),
        ...getCommonSettings(langCode),
      },
    });
  }

  return replicas;
};

const siteWideBaseIndexList = [];
function generateSiteWideSearchAlgoliaConfig(configList) {
  return {
    indexName: 'site-wide-search',
    refinements: [],
    searchableAttributes: [
      'title',
      'body',
      'teacher.full_name, topic.name, tags.text, level.name, style.name, focus.name, anatomy.name,time_of_day.name, goals.name, props.name, algolia_focus_filters.name,full_name',
    ],
    getContentItemsFromQueryResult: ({ yi }) => [
      ...yi.allArticles,
      ...yi.allClasses,
      ...yi.allPranayamaClasses,
      ...yi.allPranayamaMeditations,
      ...yi.allMeditations,
      ...yi.allPodcasts,
      ...yi.allCourses,
      ...yi.allPremiumTrainings,
      ...yi.allChallenges,
      ...yi.allTeachers,
      ...yi.allSeries,
    ],
    query(language) {
      return `{
      yi {
        allArticles:${getAllArticlesQuery(language)}
        allClasses:${getAllClassesQuery(language)}
        allPranayamaClasses:${getAllPranayamaClasses(language)}
        allPranayamaMeditations:${getAllPranayamaMeditations(language)}
        allMeditations:${getAllMeditations(language)}
        allPodcasts:${getAllPodcasts(language)}
        allCourses:${getAllCourses(language)}
        allPremiumTrainings: ${getAllPremiumTrainings(language)}
        allChallenges: ${getAllChallenges(language)}
        ${getAllTeacher(language)}
        ${getAllSeries(language)}
      }
    }`;
    },
  };
}

function generateSiteWideSearchAlgoliaConfigV2(configList) {
  return {
    indexName: 'site-wide-search-v2',
    refinements: [],
    ranking: [...defaultRanking, 'desc(entry_date.timestamp)'],
    searchableAttributes: [
      'title, body',
      'teacher.full_name, topic.name, tags.text, level.name, style.name, focus.name, anatomy.name,time_of_day.name, goals.name, props.name, algolia_focus_filters.name,full_name',
    ],
    getContentItemsFromQueryResult: ({ yi }) => [
      ...yi.allArticles,
      ...yi.allClasses,
      ...yi.allPranayamaClasses,
      ...yi.allPranayamaMeditations,
      ...yi.allMeditations,
      ...yi.allPodcasts,
      ...yi.allCourses,
      ...yi.allPremiumTrainings,
      ...yi.allChallenges,
      ...yi.allSeries,
    ],
    query(language) {
      return `{
      yi {
        allArticles:${getAllArticlesQuery(language)}
        allClasses:${getAllClassesQuery(language)}
        allPranayamaClasses:${getAllPranayamaClasses(language)}
        allPranayamaMeditations:${getAllPranayamaMeditations(language)}
        allMeditations:${getAllMeditations(language)}
        allPodcasts:${getAllPodcasts(language)}
        allCourses:${getAllCourses(language)}
        allPremiumTrainings: ${getAllPremiumTrainings(language)}
        allChallenges: ${getAllChallenges(language)}
        ${getAllSeries(language)}
      }
    }`;
    },
  };
}

const siteWideSearchConfig = generateSiteWideSearchAlgoliaConfig(algoliaConfigs);
algoliaConfigs.push(siteWideSearchConfig, generateSiteWideSearchAlgoliaConfigV2(algoliaConfigs));

languages.forEach(lang => {
  algoliaConfigs.forEach(
    ({
      getContentItemsFromQueryResult,
      indexName,
      refinements,
      query,
      searchableAttributes,
      ranking,
      attributeForDistinct,
    }) => {
      const addAlgoliaFields = item =>
        refinements.reduce(
          (newItem, { facetingAttributeName, getFieldValue }) => {
            newItem[withPrefix(facetingAttributeName)] = getFieldValue(item);
            return newItem;
          },
          { ...item },
        );

      const unretrievableAttributes = [
        ...commonUnretrievableAttributes,
        ...refinements.map(r => withPrefix(r.facetingAttributeName)),
      ];

      searchableAttributes = [
        ...searchableAttributes,
        // refinements are not ordered by priority, so we make them equal
        refinements.map(r => withPrefix(r.facetingAttributeName)).join(', '),
      ];

      const replicaIndexes = getReplicaIndices(
        indexName,
        lang.code,
        refinements,
        unretrievableAttributes,
        searchableAttributes,
        attributeForDistinct,
      );

      // Primary index
      algoliaQueries.push({
        forwardToReplicas: false,
        settings: {
          ranking: ranking || [...commonRanking],
          replicas: replicaIndexes.map(r => r.indexName),
          unretrievableAttributes,
          searchableAttributes,
          attributesForFaceting: refinements.map(r => withPrefix(r.facetingAttributeName)),
          ...(attributeForDistinct && { attributeForDistinct }),
          ...getCommonSettings(lang.code),
        },
        transformer: ({ data }) => {
          const items = getContentItemsFromQueryResult(data);

          // This field is required by the plugin which presumes querying with Gatsby's `pages.nodes` API,
          // but we do not use this. It should be a hash of the content, used to determine partial builds.
          // Since we lack this hash, we give a unique value to ensure no content is skipped.
          items.forEach(item => {
            item.internal = { contentDigest: `${Date.now()}` };
          });

          const result = items.map(addAlgoliaFields);
          return result;
        },
        query: query(lang.name),
        indexName: `${indexName}_${lang.code}`,
      });

      algoliaReplicaIndices.push(...replicaIndexes);
    },
  );
});
